import React, { useMemo, useState } from "react"
import {
  Drawer,
  Typography,
  Button,
  Divider,
  InputNumber,
  Row,
  Col,
  Empty,
  Space,
  Card,
  Badge,
  Tooltip,
  ConfigProvider,
  Tag,
  theme,
  Checkbox,
} from "antd"
import {
  ShoppingCartOutlined,
  DeleteOutlined,
  PlusOutlined,
  MinusOutlined,
  CreditCardOutlined,
  MobileOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  GlobalOutlined,
} from "@ant-design/icons"
import STORAGE from "src/lib/storage"
import { getTypeSim, SCOPE, simType } from "src/constants/constants"
import dayjs from "dayjs"

const { Title, Text } = Typography

const CartDrawer = ({
  drawerVisible,
  onClose,
  cartItems = [],
  handleQuantityChange,
  handleBuyPackage,
  handleRemoveItem,
}) => {
  const [isConfirmed, setIsConfirmed] = useState(false)
  const commissionPercent =
    Number(localStorage.getItem(STORAGE.COMMISSION_PERCENT)) || 0
  const getTotal = () =>
    cartItems.reduce(
      (sum, item) =>
        sum +
        (Number(item.package.master_price) ||
          item.package.sim_unit_price ||
          0) *
          item.quantity,
      0,
    )
  const getTotalAfterCommission = () =>
    cartItems.reduce(
      (sum, item) => sum + (item.package.sim_unit_price || 0) * item.quantity,
      0,
    )

  // 👉 Tính ngày giao hàng dự kiến
  const deliveryDate = useMemo(() => {
    if (!cartItems.length) return null
    const maxPackageType = Math.max(
      ...cartItems.map(item => Number(item.package?.package_type) || 0),
    )
    return dayjs().add(maxPackageType, "day").format("DD/MM/YYYY")
  }, [cartItems])
  const customTheme = {
    token: {
      colorPrimary: "#1890ff",
      colorPrimaryHover: "#40a9ff",
      borderRadius: 12,
      background: "#01638d",
    },
  }

  const drawerStyles = {
    header: {
      background: "linear-gradient(135deg, #01638d 0%, #01638d 100%)",
      borderBottom: "none",
      padding: "16px 20px",
      marginTop: 78,
    },
    body: {
      padding: 0,
      background: "#fafafa",
    },
  }
  const hasCommission = cartItems.some(
    item => item.package?.commission && item.package?.commission > 0,
  )

  return (
    <ConfigProvider theme={{ ...theme, ...customTheme }}>
      <Drawer
        title={
          <Space size="small" style={{ color: "white", fontSize: "18px" }}>
            <div
              style={{
                padding: "6px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShoppingCartOutlined style={{ fontSize: 20, color: "white" }} />
            </div>
            <span style={{ fontWeight: 600 }}>Giỏ hàng</span>
          </Space>
        }
        placement="right"
        onClose={onClose}
        open={drawerVisible}
        width={600}
        styles={drawerStyles}
        closeIcon={
          <span
            style={{
              color: "white",
              fontSize: "14px",
              background: "rgba(255,255,255,0.2)",
              borderRadius: "50%",
              width: "28px",
              height: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </span>
        }
      >
        {cartItems.length === 0 ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "30px 20px",
            }}
          >
            <Empty
              image={
                <div
                  style={{
                    fontSize: "48px",
                    color: "#01638d",
                    opacity: 0.3,
                    marginBottom: "12px",
                  }}
                >
                  <ShoppingCartOutlined />
                </div>
              }
              description={
                <div>
                  <Title
                    level={5}
                    style={{ color: "#595959", marginBottom: "6px" }}
                  >
                    Giỏ hàng trống
                  </Title>
                  <Text type="secondary" style={{ fontSize: "14px" }}>
                    Hãy thêm sản phẩm vào giỏ hàng!
                  </Text>
                </div>
              }
            />
          </div>
        ) : (
          <div
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            {/* Cart Items */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "16px 0px",
                background: "#fafafa",
              }}
            >
              <Space
                direction="vertical"
                size="small"
                style={{ width: "100%" }}
              >
                {cartItems.map((item, index) => (
                  <CartItemCard
                    key={
                      item.package?.packageId ||
                      item.packageId ||
                      item.package_id ||
                      index
                    }
                    item={item}
                    onQuantityChange={handleQuantityChange}
                    onRemove={handleRemoveItem}
                  />
                ))}
              </Space>
            </div>

            {/* Footer */}
            <div
              style={{
                background: "white",
                padding: "16px 20px",
                borderTop: "1px solid #f0f0f0",
                boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
              }}
              className="cart-drawer-footer"
            >
              {/* Summary */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)",
                  padding: "12px",
                  borderRadius: "8px",
                  marginBottom: "12px",
                }}
              >
                {hasCommission ? (
                  <>
                    {/* Giá gốc */}
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Text style={{ fontSize: "13px", fontWeight: 500 }}>
                          Tổng ({cartItems.length} sản phẩm):
                        </Text>
                      </Col>
                      <Col>
                        <Text
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "#cfb4b4ff",
                            textDecoration: "line-through",
                          }}
                        >
                          {getTotal().toLocaleString()} VNĐ
                        </Text>
                      </Col>
                    </Row>

                    {/* Giá sau chiết khấu */}
                    <Row
                      justify="space-between"
                      align="middle"
                      style={{ marginTop: 8 }}
                    >
                      <Col>
                        <Text style={{ fontSize: "13px", fontWeight: 500 }}>
                          Sau chiết khấu ({commissionPercent}%):
                        </Text>
                      </Col>
                      <Col>
                        <Text
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "#1890ff",
                          }}
                        >
                          {getTotalAfterCommission().toLocaleString()} VNĐ
                        </Text>
                      </Col>
                    </Row>
                  </>
                ) : (
                  // Chỉ hiển thị tổng tiền nếu không có commission
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Text style={{ fontSize: "13px", fontWeight: 500 }}>
                        Tổng ({cartItems.length} sản phẩm):
                      </Text>
                    </Col>
                    <Col>
                      <Text
                        style={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          color: "#1890ff",
                        }}
                      >
                        {getTotal().toLocaleString()} VNĐ
                      </Text>
                    </Col>
                  </Row>
                )}
              </div>
              {deliveryDate && (
                <Checkbox
                  checked={isConfirmed}
                  onChange={e => setIsConfirmed(e.target.checked)}
                  style={{ marginBottom: "12px" }}
                >
                  Dự kiến ngày nhận hàng: <b>{deliveryDate}</b>. Bạn có chắc
                  chắn muốn đặt hàng không ?
                </Checkbox>
              )}
              {/* Checkout Button */}
              <Button
                type="primary"
                size="middle"
                block
                onClick={handleBuyPackage}
                disabled={!isConfirmed}
                icon={<CreditCardOutlined />}
                style={{
                  height: "40px",
                  fontSize: "14px",
                  fontWeight: 600,
                  background:
                    "linear-gradient(135deg, #01638d 0%, #01638d 100%)",
                  border: "none",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(24, 144, 255, 0.3)",
                }}
                className="checkout-btn"
              >
                Đặt hàng ngay
              </Button>
            </div>
          </div>
        )}

        <style jsx>{`
          .checkout-btn {
            background: #1890ff;
            color: #fff;
            border: none;
            padding: 10px 20px;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .checkout-btn:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(24, 144, 255, 0.4) !important;
          }

          /* Disabled nhưng vẫn rõ ràng */
          .checkout-btn:disabled {
            background: #1890ff; /* giữ nguyên màu */
            color: #fff; /* giữ nguyên chữ */
            opacity: 0.8; /* giảm nhẹ chứ không xám hẳn */
            cursor: not-allowed;
            box-shadow: none; /* bỏ hover effect */
            transform: none;
          }

          .cart-item-card {
            transition: all 0.3s ease;
          }
          .cart-item-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          }
          body {
            padding: 0 !important;
          }
        `}</style>
      </Drawer>
    </ConfigProvider>
  )
}

const CartItemCard = ({ item, onQuantityChange, onRemove }) => {
  const [loading, setLoading] = useState(false)
  console.log(item, "item cart")
  const handleQuantityChangeWithLoading = async value => {
    setLoading(true)
    setTimeout(() => {
      onQuantityChange(item.package?.package_id, value)
      setLoading(false)
    }, 200)
  }

  // Helper function để lấy giá
  const getPrice = () => {
    return (
      Number(item.package?.master_price) || item.package?.sim_unit_price || 0
    )
  }
  const getPriceDiscount = () => {
    return Number(item.package?.sim_unit_price) || 0
  }

  // Helper function để lấy thông tin carrier
  const getCarrierInfo = () => {
    return item.package?.carrier || item.carrier || "Không có tên"
  }

  // Helper function để format dung lượng
  const formatAmount = amount => {
    if (!amount) return "N/A"
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}GB`
    }
    return `${amount}GB`
  }

  // Helper function để format thời hạn
  const formatDays = days => {
    if (!days) return "N/A"
    if (days >= 30) {
      const months = Math.floor(days / 30)
      const remainingDays = days % 30
      if (remainingDays === 0) {
        return `${months} tháng`
      }
      return `${months} tháng ${remainingDays} ngày`
    }
    return `${days} ngày`
  }

  // Helper function để lấy màu cho loại SIM
  const getSimTypeColor = simType => {
    switch (simType?.toLowerCase()) {
      case "prepaid":
      case "trả trước":
        return "blue"
      case "postpaid":
      case "trả sau":
        return "green"
      default:
        return "default"
    }
  }

  // Helper function để lấy màu cho phạm vi
  const getScopeColor = scope => {
    switch (scope?.toLowerCase()) {
      case "domestic":
      case "trong nước":
        return "orange"
      case "international":
      case "quốc tế":
        return "purple"
      default:
        return "default"
    }
  }

  return (
    <Card
      className="cart-item-card"
      style={{
        borderRadius: "12px",
        border: "1px solid #e8f4fd",
        background: "white",
        overflow: "hidden",
      }}
      bodyStyle={{ padding: "16px" }}
    >
      {/* Header với tên carrier và giá */}
      <Row justify="space-between" align="top" style={{ marginBottom: "12px" }}>
        <Col flex="auto">
          <Title
            level={5}
            style={{
              margin: "0 0 4px 0",
              color: "#262626",
              fontSize: "16px",
              lineHeight: "1.3",
            }}
          >
            {getCarrierInfo()}
          </Title>
          {/* Tags cho loại SIM và phạm vi */}
          <Space size={4} wrap>
            {item.package.type_sim && (
              <Tag
                color={getSimTypeColor(item.package.type_sim)}
                style={{ fontSize: "11px", margin: "2px 0" }}
              >
                <CreditCardOutlined style={{ marginRight: "2px" }} />
                {getTypeSim.find(t => t.key === item.package.type_sim)?.value ||
                  item.package.type_sim}
              </Tag>
            )}
            {item.package.sim_type && (
              <Tag
                color={getSimTypeColor(item.package.sim_type)}
                style={{ fontSize: "11px", margin: "2px 0" }}
              >
                <MobileOutlined style={{ marginRight: "2px" }} />
                {simType.find(t => t.key === item.package.sim_type)?.value ||
                  item.package.sim_type}
              </Tag>
            )}

            {item.package.scope && (
              <Tag
                color={getScopeColor(item.package.scope)}
                style={{ fontSize: "11px", margin: "2px 0" }}
              >
                <GlobalOutlined style={{ marginRight: "2px" }} />
                {SCOPE.find(t => t.key === item.package.scope)?.value ||
                  item.package.scope}
              </Tag>
            )}
          </Space>
        </Col>
        <Col>
          <Text
            style={{
              color: "#1890ff",
              fontWeight: 600,
              fontSize: "16px",
            }}
          >
            {getPrice().toLocaleString()} VNĐ{" "}
            {/* {Number(item?.package?.commission)
              ? `(-${item?.package?.commission}%)`
              : null} */}
          </Text>
        </Col>
      </Row>

      {/* Thông tin chi tiết gói cước */}
      <div style={{ marginBottom: "12px" }}>
        <Row gutter={8}>
          {item.package.days && (
            <Col span={12}>
              <div
                style={{
                  background: "#f6ffed",
                  padding: "6px 8px",
                  borderRadius: "6px",
                  border: "1px solid #b7eb8f",
                }}
              >
                <Space size={4} align="center">
                  <ClockCircleOutlined
                    style={{ color: "#52c41a", fontSize: "12px" }}
                  />
                  <Text style={{ fontSize: "12px", color: "#389e0d" }}>
                    {formatDays(item.package.days)}
                  </Text>
                </Space>
              </div>
            </Col>
          )}
          {item.package.amount && (
            <Col span={12}>
              <div
                style={{
                  background: "#fff7e6",
                  padding: "6px 8px",
                  borderRadius: "6px",
                  border: "1px solid #ffd591",
                }}
              >
                <Space size={4} align="center">
                  <DatabaseOutlined
                    style={{ color: "#fa8c16", fontSize: "12px" }}
                  />
                  <Text style={{ fontSize: "12px", color: "#d46b08" }}>
                    {formatAmount(item.package.amount)}
                  </Text>
                </Space>
              </div>
            </Col>
          )}
        </Row>
      </div>

      {/* Tổng tiền cho item này */}
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: "12px" }}
      >
        <Col>
          <Text type="secondary" style={{ fontSize: "13px" }}>
            Tổng tiền:
          </Text>
        </Col>
        <Col>
          {item.package?.commission ? (
            <>
              <Text
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#262626",
                  textDecoration: "line-through",
                  marginRight: 8,
                }}
              >
                {(getPrice() * item.quantity).toLocaleString()} VNĐ
              </Text>
              <Text
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#f10909ff",
                }}
              >
                {(getPriceDiscount() * item.quantity).toLocaleString()} VNĐ
                {Number(item?.package?.commission)
                  ? `(-${item?.package?.commission}%)`
                  : null}
              </Text>
            </>
          ) : (
            <Text
              style={{
                fontSize: "16px",
                fontWeight: 700,
                color: "#f10909ff",
              }}
            >
              {(getPrice() * item.quantity).toLocaleString()} VNĐ
            </Text>
          )}
        </Col>
      </Row>

      <Divider style={{ margin: "8px 0" }} />

      {/* Quantity Controls và Remove Button */}
      <Row justify="space-between" align="middle">
        {/* Quantity Controls */}
        <Col>
          <Space size="small">
            <Button
              size="small"
              icon={<MinusOutlined />}
              onClick={() =>
                handleQuantityChangeWithLoading(Math.max(1, item.quantity - 1))
              }
              disabled={item.quantity <= 1 || loading}
              style={{
                borderRadius: "6px",
                border: "1px solid #d9d9d9",
                width: "28px",
                height: "28px",
              }}
            />

            <InputNumber
              size="small"
              min={1}
              value={item.quantity}
              onChange={handleQuantityChangeWithLoading}
              loading={loading}
              style={{
                width: "50px",
                textAlign: "center",
                borderRadius: "6px",
              }}
              controls={false}
            />

            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() => handleQuantityChangeWithLoading(item.quantity + 1)}
              disabled={loading}
              style={{
                borderRadius: "6px",
                border: "1px solid #d9d9d9",
                width: "28px",
                height: "28px",
              }}
            />
          </Space>
        </Col>

        {/* Remove Button */}
        <Col>
          <Tooltip title="Xóa sản phẩm">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() =>
                onRemove(item.package?.package_id || item.package_id)
              }
              style={{
                borderRadius: "6px",
                color: "#ff4d4f",
                width: "32px",
                height: "32px",
              }}
            />
          </Tooltip>
        </Col>
      </Row>
    </Card>
  )
}

export default CartDrawer
